import React, { useEffect, useState } from 'react'
import {
  Container,
  TextCaption,
  TextTitle,
  TextBody,
  Icon,
  Button,
  State,
  RadioGroup
} from '@pmi/pmi-iqos-ds-react'
import {
  goTo,
  Routes,
  Fragments,
  ShippingTypes,
  DeliveryDateMode,
  htmlTranslation,
  registerForm,
  validateForm,
  unregisterForm,
  analytics
} from '@pmi/pmi-iqos-shared-react'
import {
  CheckoutPickup,
  CheckoutShipping,
  CheckoutDelivery
} from '@dcx-common/components'

const CheckoutStep1 = ({
  translations,
  products,
  shippingMethods,
  selectedShippingMethodIndex,
  handleSelectShippingMethod,
  pickupFailed,
  pickupRegions,
  defaultShippingAddress,
  defaultShippingAddressIsValid,
  hasTobacco,
  handleSearchPickupStores,
  pickupStoreList,
  pickupStoreListNumber,
  handleSelectPickupStore,
  selectedPickupStore,
  pickupStoreListNoResult,
  setMainStateProperty,
  showSaveAsProfileAddress,
  toggleSaveAsProfileAddress,
  updateCheckoutDeliveryOptions,
  isDefaultAddressCheckboxDisabled,
  preorder,
  enableNextStep,
  getSavedNewShippingAddress,
  handleDeliveryOptionsUpdate,
  deliveryOptions,
  loading,
  handleChangeNewShippingAddress,
  handleZipCodeNotEligibleForSagawa,
  isSagawa,
  line
}) => {
  const [toggleNewShippingAddress, setToggleNewShippingAddress] = useState(
    !defaultShippingAddressIsValid
  )
  const [newShippingAddress, setNewShippingAddress] = useState({
    postalCode: '',
    postalCodeHidden: '',
    city: '',
    state: '',
    country: '',
    addressLine1: '',
    shippingMethodId: '0'
  })
  const [newShippingAddressIsValid, setNewShippingAddressIsValid] =
    useState(false)
  const [dateMode, setDateMode] = useState(DeliveryDateMode.NO_DATE)
  const [currentDelivery, setCurrentDelivery] = useState(null)
  const [currentDeliveryTimeSlot, setCurrentDeliveryTimeSlot] = useState(null)
  const [zipCodeEligibleForSagawa, setZipCodeEligibleForSagawa] =
    useState(undefined)

  const formId = registerForm()

  useEffect(() => {
    analytics.virtualPageView('checkout details', 'checkout')
    analytics.checkoutView(products, 1, null)
    analytics.virtualPageView('shipping information', 'checkout')
    analytics.checkoutView(products, 2, 'default')
    return () => {
      unregisterForm(formId)
    }
  }, [])

  useEffect(() => {
    const isValid = validateForm(formId, true)
    const sagawaValid = !isSagawa || (isSagawa && zipCodeEligibleForSagawa)
    setNewShippingAddressIsValid(isValid && sagawaValid)
    handleChangeNewShippingAddress()
  }, [newShippingAddress])

  const handleEditTVAClick = () => {
    goTo(Routes.profile, true, false, ['editable=edit'], Fragments.delivery)
  }

  const zipCodeChangeHandler = selectedOption => {
    if (selectedOption && selectedOption.item) {
      const updatedNewShippingAddress = {
        ...newShippingAddress,
        postalCode: selectedOption.item.zip_code,
        city: selectedOption.item.city + ' ' + selectedOption.item.village,
        state: selectedOption.item.state,
        postalCodeHidden: selectedOption.item.zip_code
      }
      setNewShippingAddress(updatedNewShippingAddress)
      setDateMode(DeliveryDateMode.NO_DATE)
      setCurrentDelivery(null)
      setCurrentDeliveryTimeSlot(null)
      const zipCodeEligible = selectedOption.item.sagawa
      if (isSagawa) {
        setZipCodeEligibleForSagawa(zipCodeEligible)
        handleZipCodeNotEligibleForSagawa(zipCodeEligible)
      }
      if (!isSagawa || (isSagawa && zipCodeEligible)) {
        updateCheckoutDeliveryOptions(updatedNewShippingAddress.postalCode)
      }
    }
  }

  const handleChangeInputsNewShippingAddress = e => {
    const name = e.target.name
    const value = e.target.value
    const updatedNewShippingAddress = { ...newShippingAddress, [name]: value }
    setNewShippingAddress(updatedNewShippingAddress)
  }

  const handleChangeSaveAsProfileAddress = e => {
    setMainStateProperty(e.target.name, e.target.checked)
  }

  const handleChangeShippingAddressType = e => {
    const useDefaultAddress = e.target.value === '0'
    setToggleNewShippingAddress(!useDefaultAddress)
    setDateMode(DeliveryDateMode.NO_DATE)
    setCurrentDelivery(null)
    setCurrentDeliveryTimeSlot(null)
    updateCheckoutDeliveryOptions(
      useDefaultAddress
        ? defaultShippingAddress.postalCode
        : newShippingAddress.postalCode
    )
    setMainStateProperty('useDefaultShippingAddress', useDefaultAddress)
    setMainStateProperty('toggleSaveAsProfileAddress', false)
  }

  const updateDeliveryOption = (
    currentDelivery,
    currentDeliveryTimeSlot,
    dateMode
  ) => {
    setDateMode(dateMode)
    setCurrentDelivery(currentDelivery)
    setCurrentDeliveryTimeSlot(currentDeliveryTimeSlot)

    handleDeliveryOptionsUpdate({
      dayId:
        currentDelivery && currentDelivery.day && currentDelivery.day.leadDays,
      ...currentDeliveryTimeSlot
    })
    analytics.checkoutOption(
      2,
      (currentDeliveryTimeSlot && currentDeliveryTimeSlot.label) || 'default'
    )
  }

  const handleChangeMode = e => {
    const dateMode = e.target.value
    const defaultDelivery =
      deliveryOptions && dateMode === DeliveryDateMode.DATE_SELECTION
        ? deliveryOptions.length > 0
          ? deliveryOptions[0]
          : null
        : null
    updateDeliveryOption(
      defaultDelivery,
      defaultDelivery &&
        defaultDelivery.timeSlots &&
        defaultDelivery.timeSlots.length > 0
        ? defaultDelivery.timeSlots[0]
        : null,
      dateMode
    )
  }

  const handleChangeDate = e => {
    const date = e.target.value
    if (date instanceof Date && !isNaN(date)) {
      const filteredDeliveries =
        deliveryOptions.filter(
          delivery =>
            (delivery.day ? new Date(delivery.day.date).getTime() : null) ===
            date.getTime()
        ) || []
      const currentDelivery =
        filteredDeliveries.length > 0 ? filteredDeliveries[0] : null
      updateDeliveryOption(
        currentDelivery,
        currentDelivery &&
          currentDelivery.timeSlots &&
          currentDelivery.timeSlots.length > 0
          ? currentDelivery.timeSlots[0]
          : null,
        dateMode
      )
    }
  }

  const handleChangeHour = e => {
    const deliveryHour = parseInt(e.target.value)
    const filteredDeliveryTimeSlots =
      (currentDelivery &&
        currentDelivery.timeSlots &&
        currentDelivery.timeSlots.filter(
          timeSlot => timeSlot.hour === deliveryHour
        )) ||
      []
    const currentDeliveryTimeSlot =
      filteredDeliveryTimeSlots.length > 0 ? filteredDeliveryTimeSlots[0] : null
    updateDeliveryOption(currentDelivery, currentDeliveryTimeSlot, dateMode)
  }

  const handleShippingMethodChange = e => {
    const index = parseInt(e.target.value || '')
    if (selectedShippingMethodIndex !== index) {
      handleSelectShippingMethod && handleSelectShippingMethod(index)
    }
  }

  const isDeliverySetupCompleted = () => {
    // Pickup
    const shippingType = shippingMethods[selectedShippingMethodIndex].shipping
    if (
      shippingType === ShippingTypes.FSS ||
      shippingType === ShippingTypes.FSS_SIS_LOCKER ||
      shippingType === ShippingTypes.CSTORE
    ) {
      return selectedPickupStore !== undefined && selectedPickupStore !== null
    }

    // Delivery
    if (toggleNewShippingAddress) {
      return newShippingAddressIsValid
    }

    const hasDates =
      deliveryOptions &&
      deliveryOptions.length > 0 &&
      (
        deliveryOptions.filter(
          delivery => delivery.day && !!delivery.day.date
        ) || []
      ).length > 0

    return (
      dateMode === DeliveryDateMode.NO_DATE ||
      (dateMode === DeliveryDateMode.DATE_SELECTION &&
        currentDelivery &&
        (!hasDates ||
          (currentDelivery.day &&
            (currentDelivery.day.leadDays ||
              currentDelivery.day.leadDays === 0))) &&
        currentDeliveryTimeSlot &&
        (currentDeliveryTimeSlot.hour || currentDeliveryTimeSlot.hour === 0))
    )
  }

  const handleConfirmAddress = () => {
    const shippingType = shippingMethods[selectedShippingMethodIndex].shipping
    const isPickup =
      shippingType === ShippingTypes.FSS ||
      shippingType === ShippingTypes.FSS_SIS_LOCKER ||
      shippingType === ShippingTypes.CSTORE

    if (toggleNewShippingAddress && !isPickup) {
      getSavedNewShippingAddress(newShippingAddress, !toggleNewShippingAddress)
    } else {
      enableNextStep(0)
    }
  }

  const renderCheckoutShipping = () => {
    return (
      <CheckoutShipping
        formId={formId}
        shippingAddress={defaultShippingAddress}
        showSaveAsProfileAddress={showSaveAsProfileAddress}
        toggleSaveAsProfileAddress={toggleSaveAsProfileAddress}
        isDefaultAddressCheckboxDisabled={isDefaultAddressCheckboxDisabled}
        toggleNewShippingAddress={toggleNewShippingAddress}
        newShippingAddress={newShippingAddress}
        handleChangeShippingAddressType={handleChangeShippingAddressType}
        handleEditTVAClick={handleEditTVAClick}
        handleChangeSaveAsProfileAddress={handleChangeSaveAsProfileAddress}
        handleChangeInputsNewShippingAddress={
          handleChangeInputsNewShippingAddress
        }
        handleChangeZipCode={zipCodeChangeHandler}
        hasTobacco={hasTobacco}
        isDefaultAddressValid={defaultShippingAddressIsValid}
        translations={translations}
        loading={loading}
        isSagawa={isSagawa}
        zipCodeEligibleForSagawa={zipCodeEligibleForSagawa}
        line={line}
      />
    )
  }

  const renderCheckoutDelivery = () => {
    const hasDates =
      deliveryOptions &&
      deliveryOptions.length > 0 &&
      (
        deliveryOptions.filter(
          delivery => delivery.day && !!delivery.day.date
        ) || []
      ).length > 0

    return (
      <Container pt={['6', '8']}>
        <Container pb={['6', '4']}>
          <TextTitle heading={TextTitle.headings.h6}>
            {translations.tk_checkoutStepTitleDelivery}
          </TextTitle>
        </Container>
        {!hasDates && (
          <Container pb={['6', '4']}>
            <TextBody
              size={TextBody.sizes.m}
              {...htmlTranslation(translations, 'tk_checkoutIMSDisclamer')}
            />
          </Container>
        )}
        <TextBody
          size={TextBody.sizes.m}
          {...htmlTranslation(translations, 'tk_checkoutStepSubtitleDelivery')}
        />
        <Container pt="8">
          <CheckoutDelivery
            dateMode={dateMode}
            deliveryOptions={deliveryOptions}
            currentDelivery={currentDelivery}
            currentDeliveryTimeSlot={currentDeliveryTimeSlot}
            handleChangeMode={handleChangeMode}
            handleChangeDate={handleChangeDate}
            handleChangeHour={handleChangeHour}
            translations={translations}
            preorder={preorder}
          />
        </Container>
      </Container>
    )
  }

  const renderCheckoutPickup = () => {
    return (
      <CheckoutPickup
        translations={translations}
        pickupRegions={pickupRegions}
        defaultZipCode={defaultShippingAddress.postalCode}
        handleSearchPickupStores={handleSearchPickupStores}
        pickupStoreList={pickupStoreList}
        pickupStoreListNumber={pickupStoreListNumber}
        handleSelectPickupStore={handleSelectPickupStore}
        selectedPickupStore={selectedPickupStore}
        pickupStoreListNoResult={pickupStoreListNoResult}
      />
    )
  }

  const selectedShippingMethod = shippingMethods[selectedShippingMethodIndex]

  const shippingMethodsItems = (() => {
    return shippingMethods && shippingMethods.length > 1
      ? shippingMethods
          .filter(el => (isSagawa ? el.shipping === ShippingTypes.YAMATO : el))
          .map((el, index) => ({
            value: index,
            label: (
              <Container
                display={Container.displays.flex}
                alignItems={Container.alignItemsValues.center}
                height="10">
                <TextCaption size={TextCaption.sizes.m}>
                  {translations[
                    'tk_shippingType' + el.shipping.replaceAll('/', '_')
                  ] || el.shipping}
                </TextCaption>
              </Container>
            ),
            content: (
              <Container fullWidth>
                <TextBody
                  size={TextBody.sizes.m}
                  {...htmlTranslation(
                    translations,
                    'tk_shippingDetailsType' +
                      selectedShippingMethod.shipping.replaceAll('/', '_')
                  )}
                />
                {el.shipping === ShippingTypes.YAMATO ? (
                  <>
                    {renderCheckoutShipping()}
                    {renderCheckoutDelivery()}
                  </>
                ) : (
                  renderCheckoutPickup()
                )}
              </Container>
            ),
            selected: index === selectedShippingMethodIndex,
            disabled:
              (el.shipping === ShippingTypes.CSTORE ||
                el.shipping === ShippingTypes.FSS_SIS_LOCKER ||
                el.shipping === ShippingTypes.FSS) &&
              pickupFailed
          }))
      : []
  })()

  return (
    <Container>
      {shippingMethods.length > 1 ? (
        <Container>
          <Container pb="2">
            <TextBody
              size={TextBody.sizes.m}
              {...htmlTranslation(
                translations,
                'tk_checkoutStepSubtitleShippingMethod'
              )}
            />
          </Container>
          <RadioGroup
            name="shippingMethods"
            label=""
            style={RadioGroup.styles.border}
            columns={1}
            items={shippingMethodsItems}
            getItemLabel={item => item.label}
            getItemValue={item => item.value}
            getItemContent={item => item.content}
            isItemSelected={item => item.selected}
            onChangeHandler={handleShippingMethodChange}
            showOnlySelectedContent
            spacing={RadioGroup.spacingValues.large}
            data-testing="Checkout_RadioGroup_ShippingMethods"
          />
        </Container>
      ) : (
        <Container fullWidth px="4">
          <Container pb="8">
            <TextBody
              size={TextBody.sizes.m}
              {...htmlTranslation(
                translations,
                'tk_shippingDetailsType' +
                  selectedShippingMethod.shipping.replaceAll('/', '_')
              )}
            />
          </Container>
          {selectedShippingMethod.shipping === ShippingTypes.YAMATO ? (
            <>
              {renderCheckoutShipping()}
              {renderCheckoutDelivery()}
            </>
          ) : (
            renderCheckoutPickup()
          )}
        </Container>
      )}
      {isSagawa && (
        <Container pt="4">
          <Container
            bg={Container.backgrounds.hardWhite}
            display={Container.displays.flex}
            p="8">
            <Container pr="4" pt="2">
              <State state={State.states.warning} size={State.sizes.xs} />
            </Container>
            <Container>{translations.tk_stepOneNoticeBlock}</Container>
          </Container>
        </Container>
      )}

      <Container
        fullWidth
        display={Container.displays.flex}
        alignItems={Container.alignItemsValues.center}
        justifyContent={Container.justifyContentValues.center}
        pt="8"
        pb={['2', '0']}>
        <Button
          text={translations.tk_stepOneSaveButton}
          disabled={!isDeliverySetupCompleted() || loading}
          onClickHandler={handleConfirmAddress}
          variant={Button.variants.primary}
          icon={Icon.images.arrowDown}
          iconPosition={Button.iconPositions.left}
          data-testing="Checkout_Button_SaveStepOne"
        />
      </Container>
    </Container>
  )
}

export default CheckoutStep1
